import { useQuery } from "@tanstack/react-query";
import type { WishlistItem } from "@spare-cart/contracts/customers";
import { apiClient } from "@core/api";
import { SessionStatus, selectStatus, useSession } from "@core/auth";
import { catalogKeys } from "./catalogKeys";

// The API's pagination schema caps `limit` at 100 — this must not exceed that.
const WISHLIST_IDS_LIMIT = 100;

export const wishlistIdsKey = () => [...catalogKeys.wishlist(), "ids"] as const;

const wishlistIdsQueryOptions = (status: SessionStatus) => ({
  queryKey: wishlistIdsKey(),
  queryFn: async () => {
    const page = await apiClient.list<WishlistItem>("/wishlist", {
      query: { limit: WISHLIST_IDS_LIMIT },
    });
    return new Set(page.data.map((item) => item.product.id));
  },
  enabled: status === SessionStatus.AUTHENTICATED,
});

export const useWishlistIds = () => {
  const status = useSession(selectStatus);
  return useQuery(wishlistIdsQueryOptions(status));
};

// Selects one product's membership so a wishlist mutation only re-renders the card that
// actually flipped, not every mounted `ProductGridCard` subscribed to the same query key.
export const useIsWishlisted = (productId: string): boolean => {
  const status = useSession(selectStatus);
  const { data } = useQuery({
    ...wishlistIdsQueryOptions(status),
    select: (ids) => ids.has(productId),
  });
  return data ?? false;
};
